import React from 'react';
import StatusArrowRight from '../../../../assets/svg/status-arrow-right.svg';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import {
  formatContactRequestDate,
  formatContactRequestRole,
} from '../../hooks/useViewContactRequest';
import type { ContactRequestHistoryEntry } from '../../api/viewContactRequestApiService';
import {
  StatusBadge,
  TimelineAvatarCircle,
  TimelineConnector,
} from './viewContactRequestUI';

/**
 * The event headline for a single history entry. A `null` previous_status marks
 * the request's creation; every other entry is a status transition rendered as
 * `previous → new` badges.
 */
const EventLabelRow: React.FC<{ entry: ContactRequestHistoryEntry }> = ({
  entry,
}) => {
  const labelClass =
    'text-sm font-normal leading-5 tracking-[0.25px] text-[#181A1B] wrap-break-word';

  if (entry.previous_status === null) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>{L.VIEW_CR_HISTORY_CREATED}</span>
        <StatusBadge status={entry.new_status} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={labelClass}>{L.VIEW_CR_HISTORY_STATUS_CHANGE}</span>
      <StatusBadge status={entry.previous_status} />
      <StatusArrowRight className="shrink-0" width={16} height={16} />
      <StatusBadge status={entry.new_status} />
    </div>
  );
};

const CRHistoryEntryRow: React.FC<{
  entry: ContactRequestHistoryEntry;
  isLast: boolean;
  testId: string;
}> = ({ entry, isLast, testId }) => {
  const isCreation = entry.previous_status === null;
  const name = entry.performed_by_name;
  const role = formatContactRequestRole(entry.performed_by_role);

  return (
    <div className="flex gap-3" data-testid={testId}>
      <div className="flex flex-col items-center">
        <TimelineAvatarCircle name={name} />
        {!isLast && <TimelineConnector testId={`${testId}-connector`} />}
      </div>

      <div className="flex flex-col gap-1 min-w-0 pb-8">
        <EventLabelRow entry={entry} />
        {name && (
          <p className="text-sm font-normal leading-5 tracking-[0.25px] text-[#474A4C] w-full wrap-break-word">
            {isCreation
              ? L.VIEW_CR_HISTORY_CREATED_BY
              : L.VIEW_CR_HISTORY_UPDATED_BY}
            : {name}
            {entry.performed_by_role ? ` (${role})` : ''}
          </p>
        )}
        <p
          className="text-xs font-normal leading-4 tracking-[0.40px] text-[#474A4C]"
          data-testid={`${testId}-date`}
        >
          {formatContactRequestDate(entry.updated_at)}
        </p>
      </div>
    </div>
  );
};

/**
 * "Historia" tab — the enriched status-history timeline for a contact request,
 * ordered oldest → newest (RR-1096). Shown only on the my-assigned-task view,
 * where the endpoint returns the `history` array.
 */
const CRHistoryTab: React.FC<{
  history: ContactRequestHistoryEntry[] | undefined;
}> = ({ history }) => {
  const entries = history ?? [];

  return (
    <div
      className="border border-[#DCDDE0] rounded-lg flex flex-col flex-1 min-h-0 overflow-y-auto"
      data-testid="view-cr-history-card"
    >
      <p className="text-lg font-medium leading-6.5 text-[#474A4C] sticky top-0 bg-white px-5 pt-5 pb-4 rounded-t-lg z-10">
        {L.VIEW_CR_NAV_HISTORY}
      </p>
      {entries.length === 0 ? (
        <div
          className="flex flex-1 flex-col items-center justify-center text-center min-h-[200px] px-5 pb-5"
          data-testid="view-cr-history-empty"
        >
          <p className="text-bodyLarge font-medium text-[#474A4C]">
            {L.VIEW_CR_HISTORY_EMPTY_TITLE}
          </p>
          <p className="text-bodyMedium text-on-surface-variant mt-2 max-w-md">
            {L.VIEW_CR_HISTORY_EMPTY_DESCRIPTION}
          </p>
        </div>
      ) : (
        <div className="flex flex-col px-5 pb-5">
          {entries.map((entry, idx) => (
            <CRHistoryEntryRow
              key={entry.id || `${entry.new_status}-${entry.updated_at}-${idx}`}
              entry={entry}
              isLast={idx === entries.length - 1}
              testId={`view-cr-history-entry-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CRHistoryTab;
